import { computed, unref, Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import Contact from "@/services/contact/contact";
import { EditContact } from "@/dtos/Contact.dto";

export interface ContactParams {
  id: string;
  organizationId: string;
}

export function useContact(params: Ref<ContactParams | null> | ContactParams | null) {
  const queryKey = computed(() => {
    const p = unref(params);
    if (!p) return ["contact", "disabled"];
    return ["contact", p.id, p.organizationId];
  });

  const enabled = computed(() => {
    const p = unref(params);
    return p !== null && p !== undefined && !!p.id;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = unref(params);
      if (!p) throw new Error("Parameters not ready");

      const result = await Contact.getContacts(p);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load contact");
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contact: EditContact) => {
      const result = await Contact.editContact(contact);
      if (result.data.status) {
        return result.data.data;
      }
      throw new Error(result.data.msg || "Failed to update contact");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["buckets"] });
    },
  });
}
